import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const CGV = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = language === 'fr';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-stone-600 hover:text-stone-900 transition-colors" data-testid="cgv-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-serif text-lg font-semibold text-stone-900">
            {fr ? 'Conditions Générales de Vente' : 'Terms and Conditions'}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-stone-100 prose prose-stone prose-sm max-w-none">
          
          {fr ? (
            <>
              <h1 className="font-serif text-2xl font-bold text-stone-900 mb-2">Conditions Générales de Vente</h1>
              <p className="text-stone-400 text-xs mb-8">Dernière mise à jour : mars 2026</p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 1 — Informations légales</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Le site <strong>monikigaiia.fr</strong> est édité par la société <strong>SAS Mon Ikigai IA</strong>, 
                dont le siège social est situé au <strong>59 rue Gallion, 97233 Schœlcher</strong>.<br />
                Email de contact : <strong>monikigaiia@gmail.com</strong>
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 2 — Objet</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Les présentes Conditions Générales de Vente (CGV) régissent les ventes de produits numériques 
                proposés par la SAS Mon Ikigai IA via le site monikigaiia.fr. Tout achat implique l'acceptation 
                sans réserve des présentes CGV.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 3 — Produits et prix</h2>
              <p className="text-stone-600 text-sm leading-relaxed">Les produits numériques proposés sont :</p>
              <ul className="text-stone-600 text-sm space-y-2 mt-2">
                <li><strong>Diagnostic Ikigai IA</strong> — 1,99 € TTC : analyse personnalisée par intelligence artificielle de vos réponses aux 40 questions d'introspection, incluant un profil de personnalité, 5 pistes de carrière, 3 projets concrets, un diagramme Ikigai interactif, l'export PDF des résultats et un coaching quotidien personnalisé.</li>
                <li><strong>Plan d'Action 90 Jours</strong> — 4,99 € TTC : plan personnalisé de 12 semaines généré par l'IA, structuré en 3 phases progressives (Exploration, Construction, Lancement), avec des tâches hebdomadaires concrètes et des jalons de progression.</li>
              </ul>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                Les prix sont indiqués en euros, toutes taxes comprises (TTC). La SAS Mon Ikigai IA se réserve 
                le droit de modifier ses prix à tout moment, les produits étant facturés au prix en vigueur au 
                moment de la commande.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 4 — Commande et paiement</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Le paiement s'effectue en ligne par carte bancaire via la plateforme sécurisée <strong>Stripe</strong>. 
                Il s'agit d'un paiement unique, sans abonnement ni engagement. La commande est confirmée dès 
                réception du paiement et le produit numérique est délivré instantanément dans l'application.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 5 — Droit de rétractation et remboursement</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Conformément à l'article L.221-28 du Code de la consommation, <strong>le droit de rétractation 
                ne s'applique pas</strong> aux contrats de fourniture de contenu numérique non fourni sur un 
                support matériel dont l'exécution a commencé après accord préalable exprès du consommateur 
                et renoncement exprès à son droit de rétractation.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                En validant votre commande et en acceptant les présentes CGV, vous reconnaissez que la 
                fourniture du produit numérique commence immédiatement et vous renoncez expressément à 
                votre droit de rétractation. <strong>Aucun remboursement ne sera accordé</strong> une fois 
                le produit numérique délivré.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 6 — Livraison</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Les produits étant des contenus numériques, ils sont délivrés instantanément après confirmation 
                du paiement, directement dans l'application monikigaiia.fr. L'accès aux produits achetés est 
                disponible 24h/24, 7j/7, sous réserve de disponibilité du service.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 7 — Propriété intellectuelle</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                L'ensemble du contenu du site monikigaiia.fr (textes, images, logiciels, analyses générées) 
                est la propriété exclusive de la SAS Mon Ikigai IA. Les résultats et analyses générés pour 
                l'utilisateur sont destinés à un usage strictement personnel et ne peuvent être reproduits, 
                distribués ou commercialisés sans autorisation préalable.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 8 — Limitation de responsabilité</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Les analyses et recommandations fournies par l'intelligence artificielle sont données à titre 
                indicatif et ne constituent en aucun cas un conseil professionnel en orientation, psychologie 
                ou emploi. La SAS Mon Ikigai IA ne saurait être tenue responsable des décisions prises par 
                l'utilisateur sur la base des résultats fournis par l'application.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 9 — Protection des données personnelles</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi 
                Informatique et Libertés, la SAS Mon Ikigai IA s'engage à protéger les données personnelles 
                de ses utilisateurs. Les données collectées (email, nom, réponses aux questionnaires) sont 
                utilisées exclusivement pour le fonctionnement du service et la génération des analyses 
                personnalisées. Elles ne sont en aucun cas cédées, vendues ou partagées avec des tiers à 
                des fins commerciales.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                Conformément à la réglementation, vous disposez d'un droit d'accès, de rectification, 
                de suppression et de portabilité de vos données personnelles. Pour exercer ces droits, 
                contactez-nous à <strong>monikigaiia@gmail.com</strong>.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 10 — Droit applicable et litiges</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Les présentes CGV sont soumises au droit français. En cas de litige, une solution amiable 
                sera recherchée avant toute action judiciaire. À défaut, les tribunaux compétents du ressort 
                du siège social de la SAS Mon Ikigai IA seront seuls compétents.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                Conformément aux dispositions du Code de la consommation, le consommateur est informé qu'il 
                peut recourir à un médiateur de la consommation en cas de litige non résolu.
              </p>

              <div className="mt-10 pt-6 border-t border-stone-100">
                <p className="text-stone-400 text-xs">
                  SAS Mon Ikigai IA — 59 rue Gallion, 97233 Schœlcher — monikigaiia@gmail.com
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-serif text-2xl font-bold text-stone-900 mb-2">Terms and Conditions of Sale</h1>
              <p className="text-stone-400 text-xs mb-8">Last updated: March 2026</p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 1 — Legal Information</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                The website <strong>monikigaiia.fr</strong> is published by <strong>SAS Mon Ikigai IA</strong>, 
                registered at <strong>59 rue Gallion, 97233 Schœlcher, France</strong>.<br />
                Contact email: <strong>monikigaiia@gmail.com</strong>
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 2 — Purpose</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                These Terms and Conditions govern the sale of digital products offered by SAS Mon Ikigai IA 
                through monikigaiia.fr. Any purchase implies unreserved acceptance of these terms.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 3 — Products and Pricing</h2>
              <p className="text-stone-600 text-sm leading-relaxed">The digital products offered are:</p>
              <ul className="text-stone-600 text-sm space-y-2 mt-2">
                <li><strong>Ikigai AI Diagnostic</strong> — €1.99 incl. tax: personalized AI analysis of your responses to 40 introspection questions, including a personality profile, 5 career paths, 3 concrete projects, an interactive Ikigai diagram, PDF export, and daily personalized AI coaching.</li>
                <li><strong>90-Day Action Plan</strong> — €4.99 incl. tax: personalized 12-week plan generated by AI, structured in 3 progressive phases (Exploration, Building, Launch), with weekly tasks and progress milestones.</li>
              </ul>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                Prices are in euros, all taxes included. SAS Mon Ikigai IA reserves the right to modify 
                prices at any time. Products are billed at the price in effect at the time of purchase.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 4 — Order and Payment</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Payment is made online by credit card through the secure <strong>Stripe</strong> platform. 
                It is a one-time payment with no subscription or commitment. The order is confirmed upon 
                receipt of payment and the digital product is delivered instantly within the application.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 5 — Right of Withdrawal and Refund</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                In accordance with Article L.221-28 of the French Consumer Code, <strong>the right of 
                withdrawal does not apply</strong> to contracts for the supply of digital content not 
                provided on a tangible medium, the performance of which has begun with the consumer's 
                prior express consent and express waiver of the right of withdrawal.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                By validating your order and accepting these terms, you acknowledge that the delivery 
                of the digital product begins immediately and you expressly waive your right of withdrawal. 
                <strong>No refund will be granted</strong> once the digital product has been delivered.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 6 — Delivery</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                As digital content, products are delivered instantly after payment confirmation, directly 
                within the monikigaiia.fr application. Access to purchased products is available 24/7, 
                subject to service availability.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 7 — Intellectual Property</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                All content on monikigaiia.fr (texts, images, software, generated analyses) is the 
                exclusive property of SAS Mon Ikigai IA. Results and analyses generated for the user 
                are intended for strictly personal use and may not be reproduced, distributed, or 
                commercialized without prior authorization.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 8 — Limitation of Liability</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                The analyses and recommendations provided by artificial intelligence are for informational 
                purposes only and do not constitute professional advice in career guidance, psychology, or 
                employment. SAS Mon Ikigai IA cannot be held responsible for decisions made by the user 
                based on the results provided by the application.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 9 — Personal Data Protection</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                In accordance with the General Data Protection Regulation (GDPR), SAS Mon Ikigai IA is 
                committed to protecting its users' personal data. Data collected (email, name, questionnaire 
                responses) is used exclusively for service operation and generating personalized analyses. 
                Data is never sold, transferred, or shared with third parties for commercial purposes.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                You have the right to access, rectify, delete, and port your personal data. To exercise 
                these rights, contact us at <strong>monikigaiia@gmail.com</strong>.
              </p>

              <h2 className="font-serif text-lg font-semibold text-stone-900 mt-8 mb-3">Article 10 — Applicable Law and Disputes</h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                These terms are governed by French law. In case of dispute, an amicable solution will be 
                sought before any legal action. Failing that, the courts within the jurisdiction of SAS 
                Mon Ikigai IA's registered office shall have sole jurisdiction.
              </p>

              <div className="mt-10 pt-6 border-t border-stone-100">
                <p className="text-stone-400 text-xs">
                  SAS Mon Ikigai IA — 59 rue Gallion, 97233 Schœlcher, France — monikigaiia@gmail.com
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default CGV;
